<template>
  <div class="drawer" :class="{ 'is-disabled': disabled, 'is-nested': nested }">
    <button
      class="drawer__title"
      type="button"
      :class="[
        selected && !disabled ? 'is-selected' : '',
        data.isOpen && !disabled ? 'is-open' : ''
      ]"
      @click="toggleState"
    >
      <span class="drawer__title__text">
        {{ title }}
        <slot name="title"></slot>
      </span>
      <span class="drawer__status">
        <slot name="status"></slot>
      </span>
      <span v-if="!disabled" class="drawer__icon">
        <lucide-icon :name="data.isOpen ? 'chevron-up' : 'chevron-down'" />
      </span>
    </button>
    <transition name="drawer">
      <div v-if="data.isOpen && !disabled" class="drawer__content">
        <div class="drawer__slide">
          <slot></slot>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts" generic="N extends string">
import { reactive, watch, onBeforeMount } from "vue";
import LucideIcon from "./LucideIcon.vue";

interface Props {
  title?: string;
  open?: boolean;
  openname?: string;
  name?: N;
  selected?: boolean;
  disabled?: boolean;
  nested?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  title: "",
  open: undefined,
  openname: undefined,
  name: undefined,
  selected: false,
  disabled: false,
  nested: false
});
const emit = defineEmits<{
  (e: "open", id: typeof props.name): void;
  (e: "close", id: typeof props.name): void;
  (e: "change", id: { name: typeof props.name; open: boolean }): void;
  (e: "update:open", open: boolean): void;
}>();

const data = reactive({
  isOpen: false
});

watch(
  () => props.openname,
  () => {
    if (props.name === props.openname) {
      data.isOpen = true;
    } else {
      data.isOpen = false;
    }
  },
  { immediate: true }
);

watch(
  () => props.open,
  (val) => {
    if (val == null) return;
    data.isOpen = val;
  }
);

onBeforeMount(() => {
  if (props.name == null) {
    data.isOpen = !!props.open;
  }
});

function toggleState() {
  data.isOpen = !data.isOpen;
  if (data.isOpen === true) {
    emit("open", props.name);
  } else {
    emit("close", props.name);
  }
  emit("change", {
    name: props.name,
    open: data.isOpen
  });
  emit("update:open", data.isOpen);
}
</script>

<style lang="scss">
.drawer {
  border-bottom: 1px solid var(--color-border-light);
  position: relative;

  --padding-horizontal: 1em;
  --padding-left: var(--padding-horizontal);
  --padding-right: var(--padding-horizontal);

  .menu-group + & {
    border-top: 1px solid var(--color-border-light);
  }

  &__title {
    position: relative;
    display: flex;
    width: 100%;
    background-color: transparent;
    border: none;
    z-index: 1;

    justify-content: space-between;
    align-items: center;
    text-align: left;

    padding-top: 0.5em;
    padding-bottom: 0.5em;
    padding-left: var(--padding-left);
    padding-right: var(--padding-right);

    font-family: var(--font-family-ui);
    font-size: var(--font-size-body);
    line-height: 1.6em;
    transition:
      background-color 100ms,
      filter 100ms,
      box-shadow 100ms;

    &.is-selected {
      background-color: var(--color-action-light);
    }

    &__text {
      margin-right: auto;
      color: var(--color-text);

      .is-open > &,
      .is-selected > & {
        color: var(--color-action);
        font-weight: 700;
      }
    }
    &:hover &__text {
      font-weight: 700;
      color: var(--color-text-dark);
    }
    &.is-open:hover &__text {
      color: var(--color-action);
    }
    .is-disabled &:hover &__text {
      font-weight: 400;
    }
    &:hover .drawer__icon {
      background-color: var(--color-action-light);
    }
    &:hover.is-open .drawer__icon {
      background-color: var(--color-action-light);
    }
    &:focus {
      outline: none;
    }
    &:focus-visible &__text {
      border-bottom: 2px solid var(--color-action);
    }
    &:focus-visible .drawer__icon {
      background-color: var(--color-action);
      color: var(--color-action-contrast);
    }
    &:active {
      box-shadow: inset 0 10px 15px -15px rgba(0, 0, 0, 0.05);
    }
    &:active .drawer__icon {
      background-color: var(--color-action-light);
    }
  }

  &__icon {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0;
    width: 28px;
    height: 28px;
    font-size: 22px;
    border-radius: 50%;
    flex-shrink: 0;
    color: var(--color-text-medium);
    transition: background-color 100ms ease-out;

    .is-open > & > svg {
      margin-top: -2px;
    }
    .is-open > &,
    .is-selected > & {
      color: var(--color-action);
      background-color: var(--color-action-light);
    }
  }

  &__status {
    padding: 0 0.75rem;
  }

  &__content {
    position: relative;
    padding-bottom: 1rem;
  }

  &__slide {
    padding-bottom: 0.5em;
    padding-right: var(--padding-right);
    padding-left: var(--padding-left);
  }

  /* Enter and leave animations can use different */
  /* durations and timing functions.              */
  &-enter-active {
    transition: all 300ms ease;
  }
  &-leave-active {
    transition: all 200ms cubic-bezier(1, 0.5, 0.8, 1);
  }
  &-enter-from,
  &-leave-to {
    transform: translateY(-10px);
    opacity: 0;
  }

  &.is-nested {
    margin-right: -1em;
    margin-left: -1em;
    padding-left: 1em;
    background-color: rgba(var(--tint-contrast), 0.01);
    .drawer__title {
      color: var(--color-text);
      padding-left: 0;
      margin-left: -1em;
      padding-left: 1em;
      width: calc(100% + 1em);

      &.is-open > .drawer__title__text,
      &.is-selected > .drawer__title__text {
        color: var(--color-text);
      }

      &.is-open > .drawer__icon,
      &.is-selected > .drawer__icon {
        color: var(--color-text);
        background-color: transparent;
      }
    }
    .drawer__slide {
      padding-left: 0;
    }

    &:last-of-type {
      border-bottom: none;
    }
  }
}
</style>
